import { useMemo, ElementType } from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
  Box,
  Flex,
  Icon,
  Text,
  Button,
  useColorMode,
  VStack,
  HStack,
  Divider,
  useColorModeValue,
  Avatar,
} from '@chakra-ui/react';
import { FiHome, FiClock, FiShoppingBag, FiGrid, FiSun, FiMoon } from 'react-icons/fi';
import { Location, useLocation } from 'react-router';
import { useUserDefaultRole } from '@nhost/react';
import { Roles } from 'src/utils';
import { RiEBike2Line } from 'react-icons/ri';
import { BsReceipt } from 'react-icons/bs';

interface NavMenuItem {
  label: string;
  href: string;
  icon: ElementType;
}

interface NavItemProps {
  icon: ElementType;
  children: React.ReactNode;
  to: string;
  sidebar: {
    isOpen: boolean;
    onClose: () => void;
  };
}

// Removed ride-related menus

const adminFoodsMenus: NavMenuItem[] = [
  {
    label: 'Dashboard',
    href: 'admin',
    icon: FiHome as ElementType,
  },
  {
    label: 'Categories',
    href: 'categories',
    icon: FiGrid as ElementType,
  },
  {
    label: 'Orders',
    href: 'order',
    icon: FiShoppingBag as ElementType,
  },
  {
    label: 'History',
    href: 'history',
    icon: FiClock as ElementType,
  },
  {
    label: 'Transactions',
    href: 'transcation',
    icon: BsReceipt as ElementType,
  },
  {
    label: 'Carriers',
    href: 'carrier',
    icon: RiEBike2Line as ElementType,
  },
];

const restaurantMenus: NavMenuItem[] = [
  // {
  //   label: 'Overview',
  //   href: 'overview',
  //   icon: FiHome as ElementType,
  // },
  {
    label: 'Orders',
    href: 'order',
    icon: FiShoppingBag as ElementType,
  },
  {
    label: 'Restaurant',
    href: 'restaurant',
    icon: FiHome as ElementType,
  },

  {
    label: 'Menu',
    href: 'menu',
    icon: FiGrid as ElementType,
  },

  {
    label: 'Transactions',
    href: 'transcation',
    icon: BsReceipt as ElementType,
  },

  {
    label: 'History',
    href: 'history',
    icon: FiClock as ElementType,
  },
];

const activeRoute = (routeName: string, location: Location): boolean => {
  return location.pathname.split('/')[1] === routeName;
};

const NavItem = ({ icon: IconComponent, children, to, sidebar, ...rest }: NavItemProps) => {
  const location = useLocation();
  const isActive = activeRoute(to, location);
  const activeBg = useColorModeValue('brand.50', 'whiteAlpha.100');
  const activeColor = useColorModeValue('brand.500', 'white');
  const inactiveColor = useColorModeValue('gray.600', 'gray.400');

  const handleClick = () => {
    if (window.innerWidth < 768) {
      sidebar.onClose();
    }
  };

  return (
    <NavLink to={to}>
      <Flex
        align="center"
        p={{ base: '2.5', md: '3' }}
        mx={{ base: '1', md: '2' }}
        my={{ base: '0.5', md: '1' }}
        borderRadius="xl"
        role="group"
        cursor="pointer"
        onClick={handleClick}
        _hover={{
          bg: activeBg,
          color: activeColor,
          transform: 'translateX(2px)',
        }}
        bg={isActive ? activeBg : 'transparent'}
        color={isActive ? activeColor : inactiveColor}
        transition="all 0.2s"
        position="relative"
        {...rest}
      >
        {isActive && (
          <Box
            position="absolute"
            left={0}
            top="50%"
            transform="translateY(-50%)"
            w="3px"
            h="60%"
            bg="brand.500"
            borderRadius="full"
          />
        )}
        {IconComponent && (
          <Icon
            mr={{ base: '2', md: '3' }}
            fontSize={{ base: '16', md: '18' }}
            as={IconComponent as ElementType}
            transition="all 0.2s"
            _groupHover={{
              color: activeColor,
            }}
          />
        )}
        {sidebar.isOpen && (
          <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight={isActive ? '600' : '500'} lineHeight="1">
            {children}
          </Text>
        )}
      </Flex>
    </NavLink>
  );
};

interface SidebarContentProps {
  sidebar: {
    isOpen: boolean;
    onClose: () => void;
    onToggle: () => void;
  };
}

export const SidebarContent = ({ sidebar, ...props }: SidebarContentProps) => {
  const userDefaultRole = useUserDefaultRole();
  const { colorMode, toggleColorMode } = useColorMode();
  const menus = userDefaultRole === Roles.ADMIN ? adminFoodsMenus : restaurantMenus;
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  const ThemeIcon = colorMode === 'light' ? FiMoon : FiSun;

  const renderMenus = useMemo(
    () =>
      menus.map((menu, index) => (
        <NavItem key={index} to={menu.href} icon={menu.icon} sidebar={sidebar}>
          {menu.label}
        </NavItem>
      )),
    [menus, sidebar]
  );

  // Removed redundant food menus rendering

  return (
    <Box
      h="100%"
      overflowX="hidden"
      overflowY="auto"
      bg={bgColor}
      borderRightWidth="1px"
      borderColor={borderColor}
      w="full"
      {...props}
      css={{
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: colorMode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
          borderRadius: '24px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: colorMode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
        },
      }}
    >
      <Flex
        px={{ base: 3, md: 4 }}
        py={{ base: 4, md: 5 }}
        align="center"
        justify={sidebar.isOpen ? 'space-between' : 'center'}
        borderBottom="1px"
        borderColor={borderColor}
        mb={2}
      >
        <Link to="/map">
          <HStack spacing={3}>
            <Avatar
              size={{ base: 'sm', md: 'sm' }}
              name="Admin"
              bg="brand.500"
              color="white"
              fontWeight="bold"
              boxShadow="sm"
            />
            {sidebar.isOpen && (
              <Text
                fontSize={{ base: 'md', md: 'lg' }}
                fontWeight="bold"
                bgGradient="linear(to-r, brand.400, brand.600)"
                bgClip="text"
              >
                Magauu
              </Text>
            )}
          </HStack>
        </Link>
      </Flex>

      <VStack spacing="1" align="stretch" px="2">
        <Box py="2">{renderMenus}</Box>

        <Divider borderColor={borderColor} opacity={0.6} my="2" />

        <Box py="2">
          {/* Conditionally render the theme toggle button */}
          {sidebar.isOpen && (
            <Button
              variant="ghost"
              mx={{ base: '1', md: '2' }}
              my={{ base: '0.5', md: '1' }}
              p={{ base: '2.5', md: '3' }}
              w="90%"
              leftIcon={<Icon as={ThemeIcon as ElementType} boxSize={{ base: 4, md: 5 }} />}
              onClick={(e) => {
                e.stopPropagation();
                toggleColorMode();
                if (window.innerWidth < 768) {
                  sidebar.onClose();
                }
              }}
              justifyContent="flex-start"
              fontSize={{ base: 'xs', md: 'sm' }}
              color={textColor}
              _hover={{
                bg: useColorModeValue('brand.50', 'whiteAlpha.100'),
                color: useColorModeValue('brand.500', 'white'),
              }}
              borderRadius="xl"
            >
              {colorMode === 'light' ? 'Dark Mode' : 'Light Mode'}
            </Button>
          )}
        </Box>

        <Box py="2">{/* Additional menus can be added here if needed */}</Box>
      </VStack>
    </Box>
  );
};
